import React from 'react';
import BaseLayout from '../../layouts/BaseLayout';
import IconDetail from '../../components/IconDetail';
import { getServerSideAuth } from '../../utils/serverSideAuth';
import { kvCache } from '../../utils/kvCache';

export const runtime = 'experimental-edge';

const LoadingComponent = () => (
  <div className="p-8 text-center">
    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
    <p className="text-xl">Loading icon details...</p>
  </div>
);

export async function getServerSideProps(context) {
  const { collection: rawCollection, slug } = context.params;
  
  const authState = await getServerSideAuth(context);
  console.log(`[ICON DEBUG] 👤 Аутентификация пользователя на сервере: ${authState.isAuthenticated ? 'ДА' : 'НЕТ'}`);
  
  const collectionMap = {
    'basic-icons': 'basic',
    'outline-icons': 'outline',
    'polish-blue-icons': 'polish-blue',
    'basic3d': 'basic3d',
  };

  const collection = collectionMap[rawCollection] || rawCollection;
  
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 
                    process.env.NEXT_PUBLIC_BACK_URL || 
                    'http://localhost:8000';
  
  let similarCollections = [];
  switch (collection) {
    case 'polish-blue':
      similarCollections = ['polish-blue', 'outline',  'basic3d', 'basic'];
      break;
    case 'basic':
      similarCollections = ['basic', 'outline', 'basic3d', 'polish-blue' ];
      break;
    case 'outline':
      similarCollections = ['outline', 'basic', 'basic3d', 'polish-blue' ];
      break;
    case 'basic3d':
      similarCollections = ['basic3d', 'outline', 'basic', 'polish-blue'];
      break;
    default:
      similarCollections = ['outline', 'polish-blue', 'basic3d', 'basic'];
  }
  
  if (slug?.includes('outline') && collection !== 'outline') {
    similarCollections = ['outline', 'basic', 'polish-blue', 'basic3d'];
  }

  const defaultProps = {
    slug: slug || '',
    collection: collection || 'basic',
    backendUrl,
    iconData: null,
    imageUrl: '',
    similarIconsData: { items: [] },
    similarCollections,
    authError: false,
    error: null,
    timestamp: Date.now(),
    serverAuthState: authState
  };

  try {
    const possibleCookieNames = ['jwt_access_token', 'jwt_refresh_token', 'auth_token', 'firebase_token'];
    let authCookie = null;
    
    const cookies = context.req?.cookies || {};
    
    for (const cookieName of possibleCookieNames) {
      if (cookies[cookieName]) {
        authCookie = cookies[cookieName];
        console.log(`[ICON DEBUG] 🍪 Найден токен в куках: ${cookieName}`);
        break;
      }
    }
    
    const authHeader = context.req?.headers?.authorization;
    if (!authCookie && authHeader && authHeader.startsWith('Bearer ')) {
      authCookie = authHeader.substring(7);
      console.log(`[ICON DEBUG] 🔑 Найден токен в заголовке Authorization`);
    }
    
    if (!authCookie && authState.isAuthenticated) {
      console.log(`[ICON DEBUG] 🔐 Используем токен из серверной авторизации`);
    }
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (authCookie) {
      headers['Authorization'] = `Bearer ${authCookie}`;
    }
    
    let iconResponse;
    try {
      console.log(`[ICON DEBUG] 🌐 Запрос иконки: ${slug}`);
      iconResponse = await fetch(`${backendUrl}/asset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ slug: slug || '' }),
      });
      console.log(`[ICON DEBUG] 📡 Статус ответа: ${iconResponse.status}`);
    } catch (fetchError) {
      console.error(`[ICON DEBUG] 💥 Ошибка запроса:`, fetchError);
      return {
        props: {
          ...defaultProps,
          error: `Failed to connect to server: ${fetchError.message}`
        }
      };
    }
    
    let iconData = null;
    let authError = false;
    let serverErrorMessage = ''; 
    
    if (iconResponse.status === 401 || iconResponse.status === 403) {
      authError = true;
      console.log(`[ICON DEBUG] 🔒 Ошибка авторизации при запросе иконки`);
      
      try {
        const errorData = await iconResponse.json();
        serverErrorMessage = errorData.message || errorData.error || 
                          `Server response: ${iconResponse.status} - Authentication failed`;
      } catch (e) {
        serverErrorMessage = `Server response: ${iconResponse.status} - Authentication error`;
      }
      
      let nonAuthResponse;
      try {
        console.log(`[ICON DEBUG] 🔓 Пробуем запросить иконку без авторизации`);
        nonAuthResponse = await fetch(`${backendUrl}/asset`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ slug: slug || '' }),
        });
        console.log(`[ICON DEBUG] 📡 Статус без авторизации: ${nonAuthResponse.status}`);
      } catch (fetchError) {
        return {
          props: {
            ...defaultProps,
            authError: true,
            error: `Failed to connect to server without auth: ${fetchError.message}`
          }
        };
      }
      
      if (nonAuthResponse.ok) {
        try {
          iconData = await nonAuthResponse.json();
          iconData.error_message = serverErrorMessage;
        } catch (jsonError) {
          return {
            props: {
              ...defaultProps,
              authError: true,
              error: `Failed to parse server response: ${jsonError.message}`
            }
          };
        }
      } else {
        return {
          props: {
            ...defaultProps,
            authError: true,
            error: `Failed to fetch icon data: ${nonAuthResponse.status}`
          }
        };
      }
    } else if (!iconResponse.ok) {
      console.log(`[ICON DEBUG] ❌ Ошибка запроса иконки: ${iconResponse.status}`);
      return {
        props: {
          ...defaultProps,
          error: `Server responded with ${iconResponse.status}`
        }
      };
    } else {
      try {
        iconData = await iconResponse.json();
        console.log(`[ICON DEBUG] ✅ Иконка получена успешно`);
      } catch (jsonError) {
        return {
          props: {
            ...defaultProps,
            error: `Failed to parse icon data: ${jsonError.message}`
          }
        };
      }
    }
    
    let imageUrl = '';
    
    if (iconData) {
      if (iconData.url_large) {
        imageUrl = iconData.url_large;
      } else {
        const iconCollection = iconData.collection || collection;
        switch (iconCollection) {
          case 'basic':
            imageUrl = `https://icons-pbl.davooda.com/basic-icons/${slug}.svg`;
            break;
          case 'outline':
            imageUrl = `https://icons-pbl.davooda.com/outline/png-360px/${slug}-360x360.png`;
            break;
          case 'polish-blue':
            imageUrl = `https://icons-pbl.davooda.com/polish-blue/png-360px/${slug}-360x360.png`;
            break;
          case 'basic3d':
            imageUrl = `https://icons-pbl.davooda.com/basic3d-icons/png-512px/${slug}.png`;
            break;
          default:
            imageUrl = iconData.image_url || '';
        }
      }
      
      iconData.processedImageUrl = imageUrl;
    }
    
    let similarIconsData = { items: [] };
    
    try {
      const cacheKey = `similar:${slug}`;
      const cachedData = await kvCache.get(cacheKey);
      
      if (cachedData && cachedData.items) {
        console.log(`[KV] Cache HIT for: ${slug}`);
        similarIconsData = cachedData;
      } else {
        console.log(`[KV] Cache MISS for: ${slug}`);
        
        const similarIconsResponse = await fetch(`${backendUrl}/similar_icons`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            slug: slug || '', 
            collections: similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d'] 
          }),
        });
        
        if (similarIconsResponse.ok) {
          const responseText = await similarIconsResponse.text();
          
          try {
            const responseData = JSON.parse(responseText);
            
            if (responseData && typeof responseData === 'object') {
              if (responseData.items && Array.isArray(responseData.items)) {
                similarIconsData = responseData;
              } else if (Array.isArray(responseData)) {
                similarIconsData = { items: responseData };
              } else {
                const possibleArrayProps = Object.keys(responseData).filter(key => 
                  Array.isArray(responseData[key]) && responseData[key].length > 0
                );
                
                if (possibleArrayProps.length > 0) {
                  const arrayProp = possibleArrayProps[0];
                  similarIconsData = { items: responseData[arrayProp] };
                } else {
                  similarIconsData = { items: [] };
                }
              }
            } else {
              similarIconsData = { items: [] };
            }
            
            if (similarIconsData.items && similarIconsData.items.length > 0) {
              const saved = await kvCache.set(cacheKey, similarIconsData);
              console.log(`[KV] Cache ${saved ? 'SAVED' : 'SAVE_FAILED'} for: ${slug}`);
            }
            
          } catch (parseError) {
            similarIconsData = { items: [] };
          }
        }
      }
    } catch (similarError) {
      similarIconsData = { items: [] };
    }
    
    return {
      props: {
        slug: slug || '',
        collection: collection || 'basic',
        backendUrl,
        iconData: iconData || null,
        imageUrl: imageUrl || '',
        similarIconsData,
        similarCollections,
        authError,
        error: null,
        timestamp: Date.now(),
        serverAuthState: authState
      }
    };
  } catch (error) {
    console.error(`[ICON DEBUG] 💥 Критическая ошибка:`, error);
    return {
      props: {
        ...defaultProps,
        error: `Server error: ${error.message}`
      }
    };
  }
}

function IconDetailPage({ 
  slug, 
  collection, 
  backendUrl, 
  iconData, 
  imageUrl,
  similarIconsData,
  similarCollections,
  authError,
  error,
  serverAuthState
}) {
  const handleReload = (e) => {
    e?.preventDefault?.();
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('reload', Date.now());
      window.location.href = url.toString();
    }
  };

  const keywordsArray = iconData?.keywords?.length ? iconData.keywords : [slug || 'icon'];
  const safeKeywords = Array.isArray(keywordsArray) ? keywordsArray : [slug || 'icon'];
  const pageTitle = safeKeywords.slice(0, 3).join(', ') || slug || 'Icon Detail';
  const pageDescription = iconData 
    ? `Beautiful handcrafted ${pageTitle} icon` 
    : `Icon details for ${slug || 'icon'}`;

  return (
    <BaseLayout
      title={`${pageTitle} ${collection === 'basic3d' ? '3D' : 'vector'} icon`}
      description={pageDescription}
      showHeader={true}
      serverAuthState={serverAuthState}
    >
      <div className="page-container-with-top-margin" style={{ 
        minHeight: '700px',
        position: 'relative',
      }}>
        {error ? (
          <div className="p-4 m-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <h3 className="font-bold text-lg mb-2">Error loading icon</h3>
            <p>{error}</p>
            
            <button 
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={handleReload}
            >
              Reload page
            </button>
          </div>
        ) : (
          <IconDetail
            slug={slug || ''}
            BACKEND_URL={backendUrl}
            collection={collection || 'basic'}
            initialData={iconData}
            imageUrl={imageUrl || ''}
            similarIconsData={similarIconsData || { items: [] }}
            similarCollections={similarCollections || ['basic', 'outline', 'polish-blue', 'basic3d']}
            authError={!!authError}
            serverAuthState={serverAuthState}
          />
        )}
      </div>
    </BaseLayout>
  );
}

export default IconDetailPage;